/**
 * Cart drawer estimated-delivery messages.
 *
 * Fills each line item's `.cart-item-delivery-message` placeholder with an ETA
 * derived from the variant's location provider, reusing the shared
 * window.OGKicksDelivery calculation module. Re-runs on every cart re-render
 * (add to cart / quantity change) via a MutationObserver on <cart-drawer>,
 * because those flows replace the line item markup with fresh server HTML.
 */
(() => {
    if (window.__cartDeliveryMessageLoaded) return;
    window.__cartDeliveryMessageLoaded = true;

    const PLACEHOLDER_SELECTOR = '.cart-item-delivery-message';
    // Dataset flag marking an already-processed placeholder, so our own DOM
    // writes do not retrigger the observer into an infinite loop.
    const RENDERED_FLAG = 'deliveryRendered';

    const TRUCK_ICON =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
        'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"></path>' +
        '<path d="M15 18H9"></path>' +
        '<path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"></path>' +
        '<circle cx="17" cy="18" r="2"></circle><circle cx="7" cy="18" r="2"></circle></svg>';

    const renderPlaceholder = (el) => {
        if (el.dataset[RENDERED_FLAG]) return;

        const delivery = window.OGKicksDelivery;
        if (!delivery) return;

        el.dataset[RENDERED_FLAG] = 'true';

        const provider = el.getAttribute('data-variant-provider') || '';
        const estimate = delivery.getDeliveryEstimate(provider);

        if (!estimate) {
            // No known provider for this variant: leave the row empty/hidden.
            el.classList.remove('is-visible');
            el.innerHTML = '';
            return;
        }

        // Same promise as the PDP: the simplified copy when that variant applies,
        // otherwise the next-day copy only when the calculated date is literally
        // tomorrow, otherwise the date itself.
        const simplified = delivery.getSimplifiedCopy(provider);
        let text = `Estimated delivery: <strong>${estimate.text}</strong>`;
        if (simplified) {
            text = simplified;
        } else if (estimate.kind === 'tomorrow') {
            text = delivery.NEXT_DAY_COPY;
        }

        el.dataset.etaVariant = delivery.getEtaVariant();

        el.innerHTML =
            `<span class="cart-item-delivery-message__icon">${TRUCK_ICON}</span>` +
            `<span class="cart-item-delivery-message__text">${text}</span>`;
        el.classList.add('is-visible');
    };

    const renderAll = (root) => {
        (root || document).querySelectorAll(PLACEHOLDER_SELECTOR).forEach(renderPlaceholder);
    };

    const observeDrawer = () => {
        // <cart-drawer> is the stable ancestor: cart.js replaces cart-drawer-items
        // and the footer inside it, and cart-drawer.js rewrites #CartDrawer's inner
        // HTML on add to cart. Both surface here as childList mutations.
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) return;

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'childList' && mutation.addedNodes.length) {
                    renderAll(drawer);
                    return;
                }
            }
        });

        observer.observe(drawer, { childList: true, subtree: true });
        renderAll(drawer);
    };

    const init = () => {
        renderAll(document);
        observeDrawer();
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
        init();
    }
})();
